import type { LocationEntity } from '../../domain/entities/LocationEntity.js';
import type { AdminSuggestion, ILocationRepository, LocationCreateInput, LocationListOptions, LocationSearchResult, LocationUpdateInput, ProjectLocationTabOptions, ProjectLocationTabPage, ProjectLocationTabSegmentCounts } from '../../domain/repositories/ILocationRepository.js';
import { LocationRemoteDataSource } from '../datasources/LocationRemoteDataSource.js';
import type { LocationDto } from '../dtos/LocationDto.js';

function mapDto(dto: LocationDto): LocationEntity {
  return {
    id: dto.id,
    name: dto.name,
    code: dto.code,
    description: dto.description ?? null,
    categories: dto.categories ?? [],
    provinceCode: dto.province_code ?? null,
    provinceName: dto.province?.full_name ?? dto.province?.name ?? null,
    wardCode: dto.ward_code ?? null,
    wardName: dto.ward?.full_name ?? dto.ward?.name ?? null,
    addressLine: dto.address_line,
    fullAddressCache: dto.full_address_cache,
    latitude: dto.latitude ?? null,
    longitude: dto.longitude ?? null,
    photos: dto.photos ?? [],
    metadata: dto.metadata ?? {},
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by,
  };
}

export class LocationRepository implements ILocationRepository {
  constructor(private readonly ds: LocationRemoteDataSource) {}

  async list(options?: LocationListOptions): Promise<{ data: LocationEntity[]; total: number }> {
    const { data, total } = await this.ds.list(options ?? {});
    return { data: data.map(mapDto), total };
  }

  async listByIdsInProject(
    orgId: string,
    projectId: string,
    locationIds: string[],
  ): Promise<LocationEntity[]> {
    const rows = await this.ds.listByIdsInProject(orgId, projectId, locationIds);
    return rows.map(mapDto);
  }

  async listProjectLocationTabPage(options: ProjectLocationTabOptions): Promise<ProjectLocationTabPage> {
    const result = await this.ds.listProjectLocationTabPage(options);
    return {
      ...result,
      data: result.data.map(mapDto),
    };
  }

  async countProjectLocationTabSegments(
    options: Omit<ProjectLocationTabOptions, 'segment' | 'limit' | 'offset'>,
  ): Promise<ProjectLocationTabSegmentCounts> {
    return this.ds.countProjectLocationTabSegments(options);
  }

  async listProjectLocationTabCategories(options: {
    orgId: string;
    projectId: string;
    segment: 'all' | 'sub' | 'none';
  }): Promise<string[]> {
    return this.ds.listProjectLocationTabCategories(options);
  }

  async getById(id: string): Promise<LocationEntity | null> {
    const dto = await this.ds.getById(id);
    return dto ? mapDto(dto) : null;
  }

  async create(input: LocationCreateInput): Promise<LocationEntity> {
    const dto = await this.ds.create({
      name: input.name,
      code: input.code,
      address_line: input.addressLine,
      full_address_cache: input.fullAddressCache,
      description: input.description ?? null,
      categories: input.categories ?? [],
      province_code: input.provinceCode ?? null,
      ward_code: input.wardCode ?? null,
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
      photos: input.photos ?? [],
    });
    if (!dto) throw new Error('Location creation failed');
    return mapDto(dto);
  }

  async update(id: string, input: LocationUpdateInput): Promise<LocationEntity | null> {
    const payload: Partial<LocationDto> = {};
    if (input.name !== undefined) payload.name = input.name;
    if (input.code !== undefined) payload.code = input.code;
    if (input.addressLine !== undefined) payload.address_line = input.addressLine;
    if (input.fullAddressCache !== undefined) payload.full_address_cache = input.fullAddressCache;
    if (input.description !== undefined) payload.description = input.description;
    if (input.categories !== undefined) payload.categories = input.categories;
    if (input.provinceCode !== undefined) payload.province_code = input.provinceCode;
    if (input.wardCode !== undefined) payload.ward_code = input.wardCode;
    if (input.latitude !== undefined) payload.latitude = input.latitude;
    if (input.longitude !== undefined) payload.longitude = input.longitude;
    if (input.photos !== undefined) payload.photos = input.photos;
    const dto = await this.ds.update(id, payload);
    return dto ? mapDto(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.ds.delete(id);
  }

  async listByCodes(codes: string[]): Promise<LocationSearchResult[]> {
    const rows = await this.ds.listByCodes(codes);
    return rows.map((r) => ({ id: r.id, name: r.name, code: r.code, fullAddressCache: r.full_address_cache }));
  }

  async searchLocations(orgId: string, query: string, limit?: number): Promise<LocationSearchResult[]> {
    const rows = await this.ds.searchLocations(orgId, query, limit);
    return rows.map((r) => ({ id: r.id, name: r.name, code: r.code, fullAddressCache: r.full_address_cache }));
  }

  async searchLocationsByScope(options: {
    orgId: string;
    query: string;
    projectId?: string;
    limit?: number;
    offset?: number;
    scope?: 'any' | 'inProject' | 'inOrg' | 'global' | 'admin_geo';
  }): Promise<any[]> {
    return this.ds.searchLocationsByScope(options);
  }

  async searchLocationsBatch(orgId: string, names: string[]): Promise<Map<string, LocationSearchResult | null>> {
    const raw = await this.ds.searchLocationsBatch(orgId, names);
    const map = new Map<string, LocationSearchResult | null>();
    for (const name of names) {
      const r = raw[name];
      map.set(name, r ? { id: r.id, name: r.name, code: r.code, fullAddressCache: r.full_address_cache } : null);
    }
    return map;
  }

  async autocompleteAdminSuggestions(orgId: string, query: string): Promise<AdminSuggestion[]> {
    return this.ds.autocompleteAdminSuggestions(orgId, query);
  }

  async listProvinces(): Promise<{ code: string; name: string; full_name: string }[]> {
    return this.ds.listProvinces();
  }
}
